import type { FC } from 'react';
import { useMemo } from 'react';
import { Form, type FormItemProps } from 'antd';
import i18n from 'i18next';
import { parseAbsolute, toCalendarDate } from '@internationalized/date';

import { DatePicker as DatePickerUI, type ISOTimezone } from '@nvision/ui';

import { isRequiredField } from '@/utilz';
import { useUserPreferences } from '@/modules/auth';
import { getLocalTimeZone } from '@/utils/date';

// ─────────────────────────────────────────────
// Antd Form.Item wrapper
// Temporal wrapper to support antd form logic и legacy styles.
// Remove after migration to tanstack-form.
// ─────────────────────────────────────────────

interface FormItemWrapperProps extends Pick<FormItemProps, 'rules' | 'name'> {
  label?: React.ReactNode;
  margin?: string;
  singleLineMode?: boolean;
  showValidation?: boolean;
  isFieldRequired?: boolean;
  children: React.ReactNode;
  _timeZone?: ISOTimezone;
}

const FormItemWrapper: FC<FormItemWrapperProps> = ({
  name,
  rules,
  label,
  margin = 'mb-15',
  singleLineMode = false,
  showValidation = false,
  isFieldRequired = false,
  children,
}) => (
  <Form.Item
    className={[
      singleLineMode ? 'single-line-mode' : '',
      'form-input-container',
      margin,
      showValidation ? 'show-validation' : '',
    ]
      .filter(Boolean)
      .join(' ')}
    name={name}
    rules={rules}
    {...(label
      ? {
          label: (
            <span className={isFieldRequired ? 'fw-700' : ''}>
              {label}
              {isFieldRequired && <span className="required">*</span>}
            </span>
          ),
        }
      : {})}
  >
    {children}
  </Form.Item>
);

// ─────────────────────────────────────────────
// DatePicker
// ─────────────────────────────────────────────

interface DatePickerProps extends Pick<FormItemProps, 'rules' | 'name'> {
  label?: string;
  margin?: string;
  editMode?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  tabIndex?: number;
  onChange?: (isoDate: string | null) => void;
  disabledTargetDate?: string;
  disableFuture?: boolean;
  singleLineMode?: boolean;
  showValidation?: boolean;
  isRequired?: boolean;
  dataTestId?: string;
}

const DatePicker: FC<DatePickerProps> = ({
  label,
  margin = 'mb-15',
  name = `datePicker-${Math.random()}`,
  rules = [],
  editMode = true,
  disabled = false,
  allowClear = true,
  tabIndex,
  onChange,
  disabledTargetDate,
  disableFuture = false,
  singleLineMode = false,
  showValidation = false,
  isRequired = false,
  dataTestId,
}) => {
  const isFieldRequired = (rules.length > 0 && isRequiredField(rules)) || isRequired;

  const { dateFormat } = useUserPreferences();

  const timeZone = getLocalTimeZone();

  const maxValue = useMemo(() => {
    if (!disabledTargetDate || !disableFuture) {
      return undefined;
    }

    return toCalendarDate(parseAbsolute(disabledTargetDate, timeZone));
  }, [disabledTargetDate, disableFuture, timeZone]);

  return (
    <FormItemWrapper
      name={name}
      rules={rules}
      label={label}
      margin={margin}
      singleLineMode={singleLineMode}
      showValidation={showValidation}
      isFieldRequired={isFieldRequired}
    >
      <DatePickerUI
        isDisabled={!editMode || disabled}
        isRequired={isFieldRequired}
        clearable={allowClear}
        timeZone={timeZone as ISOTimezone}
        onChange={onChange}
        format={dateFormat}
        size="sm"
        maxValue={maxValue}
        dataTestId={dataTestId}
        aria-label={label ?? i18n.t('translation:default.date')}
        {...(tabIndex != null ? { tabIndex } : {})}
      />
    </FormItemWrapper>
  );
};

export default DatePicker;
